import { UniqueIdentifier } from "@dnd-kit/core";
import { OrderTrackingOrdersSystemView } from "@redotech/order-tracking-common/default-order-tracking-order-views";
import { OrderTrackingShipmentsSystemView } from "@redotech/order-tracking-common/default-order-tracking-shipment-views";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { OrderTrackingOrderViewsContext } from "@redotech/redo-merchant-marketing-app/order-tracking/orders/order-tracking-order-views";
import { OrderTrackingShipmentViewsContext } from "@redotech/redo-merchant-marketing-app/order-tracking/shipments/order-tracking-shipment-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import {
  MarketingCloudStatusesContext,
  ORDER_TRACKING_ICON,
  ORDER_TRACKING_ICON_FILLED,
} from "../../../../redo-suite/marketing-cloud";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";

export const OrderTrackingItems = memo(function OrderTrackingItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { orderTrackingEnabled } = useRequiredContext(
    MarketingCloudStatusesContext,
  );

  const trackingBaseUrl = routes.marketingBaseUrl + "/tracking";
  const location = useLocation();
  const anyChildSelected = !!matchPath(
    `${trackingBaseUrl}/*`,
    location.pathname,
  );

  const viewedAboutTrackingPage = localStorage.getItem(
    "viewed_about_tracking_page",
  );

  return (
    <>
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getTrackingSubRoute("about") }}
        FilledIcon={ORDER_TRACKING_ICON_FILLED}
        friendlyName="Order tracking"
        hidden={orderTrackingEnabled}
        id="order-tracking-about"
        isNew={!viewedAboutTrackingPage}
        UnfilledIcon={ORDER_TRACKING_ICON}
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: trackingBaseUrl }}
        anyChildSelected={anyChildSelected}
        FilledIcon={ORDER_TRACKING_ICON_FILLED}
        friendlyName="Order tracking"
        hidden={!orderTrackingEnabled}
        id="order-tracking"
        UnfilledIcon={ORDER_TRACKING_ICON}
      >
        <OrderViewsNavItems />
        <ShipmentViewsNavItems />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getTrackingSubRoute("tracking-page"),
          }}
          friendlyName="Tracking pages"
          id="tracking-page"
        />
      </RedoSecondaryNavItem>
    </>
  );
});

const OrderViewsNavItems = memo(function OrderViewsNavItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const viewsContext = useContext(OrderTrackingOrderViewsContext);
  const urlPrefix = routes.getTrackingSubRoute("orders");
  const anyChildSelected = !!matchPath(
    `${urlPrefix}/*`,
    useLocation().pathname,
  );

  const views = useMemo(() => viewsContext?.views ?? [], [viewsContext?.views]);
  const folders = useMemo(
    () => viewsContext?.folders ?? [],
    [viewsContext?.folders],
  );

  const viewsWithoutFolders = useMemo(() => {
    return views.filter((view) => !view.folderId);
  }, [views]);

  const foldersWithViews = useMemo<
    FolderWithView<"order-tracking-orders">[]
  >(() => {
    return folders.map((folder) => ({
      folder,
      views: views.filter((view) => view.folderId === folder._id),
    }));
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    viewsContext?.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"order-tracking-orders">(
      flattenedItems,
      views,
      folders,
    );
    viewsContext?.updateOrdering(result.views, result.folders);
  });

  if (!viewsContext?.hasInitiallyLoaded) {
    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${urlPrefix}/${OrderTrackingOrdersSystemView.ALL}`,
        }}
        anyChildSelected={anyChildSelected}
        friendlyName="Orders"
        id="orders"
      />
    );
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${OrderTrackingOrdersSystemView.ALL}`,
      }}
      anyChildSelected={anyChildSelected}
      friendlyName="Orders"
      id="orders"
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            viewsContext?.openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="orders-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});

const ShipmentViewsNavItems = memo(function ShipmentViewsNavItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const viewsContext = useContext(OrderTrackingShipmentViewsContext);
  const urlPrefix = routes.getTrackingSubRoute("shipments");
  const anyChildSelected = !!matchPath(
    `${urlPrefix}/*`,
    useLocation().pathname,
  );

  const views = useMemo(() => viewsContext?.views ?? [], [viewsContext?.views]);
  const folders = useMemo(
    () => viewsContext?.folders ?? [],
    [viewsContext?.folders],
  );

  const viewsWithoutFolders = useMemo(() => {
    return views.filter((view) => !view.folderId);
  }, [views]);

  const foldersWithViews = useMemo<
    FolderWithView<"order-tracking-shipments">[]
  >(() => {
    return folders.map((folder) => ({
      folder,
      views: views.filter((view) => view.folderId === folder._id),
    }));
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    viewsContext?.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"order-tracking-shipments">(
      flattenedItems,
      views,
      folders,
    );
    viewsContext?.updateOrdering(result.views, result.folders);
  });

  if (!viewsContext?.hasInitiallyLoaded) {
    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${urlPrefix}/${OrderTrackingShipmentsSystemView.ALL}`,
        }}
        anyChildSelected={anyChildSelected}
        friendlyName="Shipments"
        id="shipments"
      />
    );
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${OrderTrackingShipmentsSystemView.ALL}`,
      }}
      anyChildSelected={anyChildSelected}
      friendlyName="Shipments"
      id="shipments"
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            viewsContext?.openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="shipments-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});
